import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { FiActivity, FiSearch, FiTerminal, FiAlertCircle, FiInfo } from 'react-icons/fi';
import { format } from 'date-fns';

export default function AdminLogs() {
  const { adminUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await adminApi.getLogs(adminUser.admin_session_token);
        if (res.success) setLogs(res.logs || []);
      } catch (e) {
        console.error('Error fetching logs:', e);
      } finally { 
        setLoading(false); 
      }
    };
    fetchLogs();
  }, [adminUser]);

  const filtered = logs.filter(l => 
    `${l.message} ${l.client_business_name} ${l.type}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="animate-pulse space-y-8"><div className="h-10 w-48 bg-slate-200 rounded-lg"></div><div className="h-[500px] bg-white rounded-[2.5rem]"></div></div>;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Audit Stream</h1>
        <p className="text-slate-500 font-bold text-sm mt-1 uppercase tracking-widest opacity-60">System Forensics</p>
      </header>

      <div className="bg-white rounded-[1.5rem] p-3 shadow-sm border border-slate-100 flex items-center gap-3">
        <div className="flex-grow relative group">
          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-300 group-focus-within:text-brand-blue transition-colors">
            <SafeIcon icon={FiSearch} />
          </div>
          <input 
            type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search audit trail for specific events or clients..." 
            className="w-full pl-12 pr-4 py-3.5 bg-slate-50 rounded-xl text-xs font-bold outline-none border-2 border-transparent focus:border-brand-blue/10 transition-all"
          />
        </div>
      </div>

      <div className="bg-navy-900 rounded-[2.5rem] shadow-2xl p-6 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="py-4 px-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Timestamp</th>
                <th className="py-4 px-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Type</th>
                <th className="py-4 px-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Client</th>
                <th className="py-4 px-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Event Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((log, i) => (
                <tr key={i} className="hover:bg-white/5 transition-all">
                  <td className="py-4 px-6">
                    <span className="text-[10px] font-mono text-slate-400">{format(new Date(log.timestamp), 'HH:mm:ss.SSS')}</span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                      log.level === 'ERROR' ? 'bg-red-500/20 text-red-400' :
                      log.level === 'WARN' ? 'bg-amber-500/20 text-amber-400' : 'bg-brand-cyan/20 text-brand-cyan'
                    }`}>
                      {log.type}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-[10px] font-bold text-white">{log.client_business_name || 'System'}</span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                       <SafeIcon icon={log.level === 'ERROR' ? FiAlertCircle : FiInfo} className={log.level === 'ERROR' ? 'text-red-400' : 'text-slate-500'} />
                       <span className="text-xs font-medium text-slate-300">{log.message}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}